import React, { useContext } from 'react';
import { Button } from 'react-bootstrap';
import { Form, FormikProps } from 'formik';
import {
    AsyncSelectField,
    CheckboxField,
    DateField,
    ResponseError,
    SelectField,
    SubmitButton,
    TextAreaField,
} from '@nintendo/forms';
import moment from 'moment-timezone';
import { useAsync } from 'react-commons';
import { LoruleRequestConfig } from '@galaxy/lorule-react-commons';
import { FormType } from '../../../common/types/formTypes';
import { BudgetThresholdType } from '../../../../api/budgetThresholdApiTypes';
import { DATE_ONLY_FORMAT, SERVER_TIMEZONE } from '../../../../utils/Constants';
import { createBudgetThreshold, updateBudgetThreshold } from '../../../../api/budgetThresholdApi';
import ConfigContext from '../../../../contexts/ConfigContext';
import Spinner from '../../../common/Spinner';
import { getTemplateRoles } from '../../../../api/projectRoleApi';
import { ProjectRoleType } from '../../../../api/projectRoleApiTypes';
import { getOrganizations } from '../../../../api/amOrganizationApi';
import { getAmUsers } from '../../../../api/amUserApi';
import { noOptionsHandler, sendToast } from '../../../../utils/displayUtils';
import CurrencyInputField from '../../../common/CurrencyInputField';

export interface BudgetThresholdFormProps extends FormikProps<BudgetThresholdType> {
    formType: FormType;
    responseError: string;
    setResponseError: (errorMessage: string) => void;
    toggleModal: () => void;
}

export const submitBudgetThreshold = async (
    config: LoruleRequestConfig,
    formType: FormType,
    values: BudgetThresholdType,
    setResponseError: (errorMessage: string) => void,
    resetForm: () => void,
    setSubmitting: (isSubmitting: boolean) => void,
    toggleModal: () => void,
) => {
    try {
        const budgetThreshold: BudgetThresholdType = {
            id: formType === FormType.UPDATE ? values.id : undefined,
            description: values.description,
            thresholdAmount: values.thresholdAmount,
            personId: values.personId,
            organizationId: values.organizationId,
            projectRoleId: values.projectRoleId,
            startDate: values.startDate ? values.startDate.toString() : '',
            endDate: values.endDate ? values.endDate.toString() : undefined,
            active: values.active,
        };
        if (formType === FormType.CREATE || formType === FormType.CLONE) {
            await createBudgetThreshold(config, budgetThreshold);
            sendToast(
                <>
                    Created Budget Threshold: <strong>{values.organizationName}</strong>
                </>,
            );
        } else if (formType === FormType.UPDATE && budgetThreshold.id) {
            await updateBudgetThreshold(config, budgetThreshold, budgetThreshold.id);
            sendToast(
                <>
                    Updated Budget Threshold: <strong>{values.organizationName}</strong>
                </>,
            );
        }
        resetForm();
        toggleModal();

        return true;
    } catch (error) {
        if (error instanceof Error) {
            setResponseError(`${formType.toLowerCase()} failed:  ${error.message}`);
        }
        setSubmitting(false);
        return false;
    }
};

const BudgetThresholdForm = (props: BudgetThresholdFormProps) => {
    const { values, errors, touched, isSubmitting, setFieldValue } = props;
    const config = useContext(ConfigContext);

    const { value: templateRoles, loading: loadingTemplateRoles } = useAsync<
        Array<ProjectRoleType>
    >(() => getTemplateRoles(config), [] as Array<ProjectRoleType>);

    const budgetThresholdRoles = templateRoles.filter(
        (projectRole) =>
            projectRole.projectRoleMeaning !== 'Project Approver' &&
            (projectRole.projectRoleMeaning.includes('Template Approver') ||
                projectRole.projectRoleMeaning.includes('Project')),
    );

    return (
        <div className="modal-content" data-testid="eventGroup-modal-content">
            <div className="modal-header">
                <h4 className="modal-title">
                    {props.formType === FormType.UPDATE ? 'Edit' : 'Create'} Budget Threshold
                </h4>
                <button type="button" className="close" data-dismiss="modal" aria-label="Close">
                    <span aria-hidden="true" onClick={props.toggleModal}>
                        &times;
                    </span>
                </button>
            </div>
            <Form className="form-horizontal">
                <div className="modal-body">
                    <ResponseError errorMessage={props.responseError} />
                    {!loadingTemplateRoles ? (
                        <>
                            <CheckboxField
                                label="Active"
                                name="active"
                                touched={touched.active}
                                errors={errors.active}
                                isRequired
                                checkboxLabel="Active"
                                testId="active"
                            />

                            {props.formType === FormType.CREATE ? (
                                <AsyncSelectField
                                    asyncSelectProps={{
                                        cacheOptions: true,
                                        loadOptions: async (inputValue: string) => {
                                            if (inputValue.length > 2) {
                                                const organizations = await getOrganizations(
                                                    config,
                                                    inputValue,
                                                );
                                                if (organizations) {
                                                    return organizations.map((organization) => ({
                                                        value: organization.id.toString(),
                                                        label: organization.name,
                                                    }));
                                                }
                                            }
                                            return [];
                                        },
                                        placeholder: values.organizationName,
                                        noOptionsMessage: noOptionsHandler,
                                    }}
                                    label="Organization"
                                    name="organizationId"
                                    touched={touched.organizationId}
                                    errors={errors.organizationId}
                                    testId="organizationId"
                                    isRequired
                                    onChange={(value) => {
                                        if (value) {
                                            setFieldValue('organizationName', value.label);
                                        } else {
                                            setFieldValue('organizationName', '');
                                        }
                                    }}
                                />
                            ) : (
                                <div className="form-group row">
                                    <div className="col-sm-5 control-label">Organization</div>
                                    <div className="col-sm-7">{props.values.organizationName}</div>
                                </div>
                            )}

                            {props.formType === FormType.CREATE ||
                            props.formType === FormType.CLONE ? (
                                <AsyncSelectField
                                    label="Person"
                                    name="personId"
                                    touched={touched.personId}
                                    errors={errors.personId}
                                    testId="personId"
                                    isRequired
                                    asyncSelectProps={{
                                        loadOptions: async (inputValue: string) => {
                                            if (inputValue.length > 2) {
                                                const users = await getAmUsers(config, {
                                                    name: inputValue,
                                                });
                                                if (users) {
                                                    return users.map((user) => ({
                                                        value: user.personId.toString(),
                                                        label: `${user.preferredName} (${user.title})`,
                                                    }));
                                                }
                                            }
                                            return [];
                                        },
                                        placeholder: values.personName,
                                        noOptionsMessage: noOptionsHandler,
                                    }}
                                />
                            ) : (
                                <div className="form-group row">
                                    <div className="col-sm-5 control-label">Person</div>
                                    <div className="col-sm-7">{props.values.personName}</div>
                                </div>
                            )}

                            <CurrencyInputField
                                label="Amount"
                                name="thresholdAmount"
                                touched={touched.thresholdAmount}
                                errors={errors.thresholdAmount}
                                testId="thresholdAmount"
                                isRequired
                            />
                            <SelectField
                                label="Project Role"
                                name="projectRoleId"
                                options={budgetThresholdRoles.map(
                                    (projectRole: ProjectRoleType) => ({
                                        name: projectRole.projectRoleMeaning,
                                        id: projectRole.projectRoleId,
                                    }),
                                )}
                                touched={touched.projectRoleId}
                                errors={errors.projectRoleId}
                                isRequired
                                testId="projectRoleId"
                            />

                            <TextAreaField
                                label="Description"
                                name="description"
                                touched={touched.description}
                                errors={errors.description}
                                testId="description"
                            />
                            <DateField
                                label="Start Date"
                                name="startDate"
                                flatpickrProps={{
                                    options: {
                                        altInputClass: 'form-control',
                                        altFormat: DATE_ONLY_FORMAT,
                                        enableTime: false,
                                        dateFormat: DATE_ONLY_FORMAT,
                                        static: true,
                                    },
                                    value: values.startDate
                                        ? moment(values.startDate).format(DATE_ONLY_FORMAT)
                                        : undefined,
                                }}
                                timezone={SERVER_TIMEZONE}
                                touched={touched.startDate}
                                errors={errors.startDate}
                                isRequired
                                testId="startDate"
                            />
                            <DateField
                                label="End Date"
                                name="endDate"
                                flatpickrProps={{
                                    options: {
                                        altInputClass: 'form-control',
                                        altFormat: DATE_ONLY_FORMAT,
                                        enableTime: false,
                                        dateFormat: DATE_ONLY_FORMAT,
                                        static: true,
                                    },
                                    value: values.endDate
                                        ? moment(values.endDate).format(DATE_ONLY_FORMAT)
                                        : undefined,
                                }}
                                timezone={SERVER_TIMEZONE}
                                touched={touched.endDate}
                                errors={errors.endDate}
                                testId="endDate"
                            />
                        </>
                    ) : (
                        <Spinner />
                    )}
                </div>

                {/* Footer */}

                <div className="modal-footer">
                    <Button
                        variant="button"
                        className="btn-outline-secondary mr-auto"
                        onClick={props.toggleModal}
                        disabled={isSubmitting}
                    >
                        Close
                    </Button>
                    <SubmitButton
                        isSubmitting={isSubmitting}
                        buttonText={props.formType === FormType.UPDATE ? 'Update' : 'Save'}
                    />
                </div>
            </Form>
        </div>
    );
};

export default BudgetThresholdForm;
